import { useQuery } from "@apollo/client";
import {
  Badge,
  Box,
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  Button,
  Flex,
  Image,
  SimpleGrid,
  Stack,
  Tab,
  TabList,
  TabPanel,
  TabPanels,
  Tabs,
  Text,
  useMediaQuery,
  VStack,
} from "@chakra-ui/react";
import { capitalize, get, isEmpty, startCase } from "lodash";
import React from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import UserLayout from "../../../../layout/UserLayout";
import { PRODUCT_CATEGORIES, UNITS } from "../../../../utils/constants";
import { GET_COMMODITY } from "./queries";
import PriceHistory from "./PriceHistory";
import { GET_COMMODITY_PUBLIC } from "../../../public/queries";
import PublicCommoditiesTable from "../../../../components/tables/PublicCommoditiesTable";
import { ArrowBackIcon } from "@chakra-ui/icons";
import { Helmet } from "react-helmet";
import { FaMapMarkerAlt } from "react-icons/fa";

type CommodityDetailsProps = {
  isPublic?: boolean;
};

const CommodityDetails = (props: CommodityDetailsProps) => {
  const location = useLocation();
  const isPublic = location.pathname.split("/").includes("public");

  const [isLargerThan900] = useMediaQuery("(min-width: 900px)");

  const navigate = useNavigate();
  const params = useParams();

  const id = get(params, "id", undefined);

  const { data, loading } = useQuery(
    isPublic ? GET_COMMODITY_PUBLIC : GET_COMMODITY,
    {
      variables: {
        id,
        range: {},
      },
      fetchPolicy: "network-only",
      notifyOnNetworkStatusChange: true,
    }
  );

  const commodity = get(data, isPublic ? "publicCommodity" : "commodity");
  const categories = get(commodity, "categories", []);

  const nestedOptions: any = [];
  UNITS.forEach((option: any) =>
    option?.options.forEach((category: any) => {
      nestedOptions.push(category);
    })
  );

  const categories_label: any = [];
  if (!isEmpty(PRODUCT_CATEGORIES.map((option: any) => option.options))) {
    PRODUCT_CATEGORIES.forEach((option: any) =>
      option?.options.forEach((category: any) => {
        categories_label.push(category);
      })
    );
  }

  const value = nestedOptions.find(
    (option: any) => option.value === commodity?.measurementUnit
  );

  return (
    <UserLayout>
      {!loading && (
        <>
          <Helmet>
            <title>Freeshu | {commodity?.name}</title>
          </Helmet>
          <Flex justifyContent="space-between" alignItems="center" mt="3">
            <Breadcrumb>
              <BreadcrumbItem>
                <BreadcrumbLink onClick={() => navigate("/")}>
                  Commodities
                </BreadcrumbLink>
              </BreadcrumbItem>

              <BreadcrumbItem>
                <BreadcrumbLink>{commodity?.name}</BreadcrumbLink>
              </BreadcrumbItem>
            </Breadcrumb>
            <Button
              size={isLargerThan900 ? "md" : "sm"}
              onClick={() => navigate("/")}
              leftIcon={<ArrowBackIcon />}
            >
              Back
            </Button>
          </Flex>
          <Flex justifyContent="space-between">
            <Flex p="1" flexDirection="row" alignItems="center">
              <Image
                src={commodity?.imageURL}
                alt={commodity?.name}
                boxSize="120px"
                fallbackSrc="https://via.placeholder.com/150"
                rounded="sm"
              />
              <Flex p="3" flexDirection="column" alignItems="start">
                <Text fontSize="2xl" mb="3">
                  {commodity?.name || ""}
                </Text>

                <Badge
                  variant="solid"
                  colorScheme="gray"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                >
                  {startCase(
                    commodity?.user?.establishment || commodity?.user?.name
                  )}
                </Badge>
                <Badge
                  variant="solid"
                  colorScheme="gray"
                  display="flex"
                  justifyContent="center"
                  alignItems="center"
                  my="2"
                >
                  {value?.label}
                </Badge>
                <Flex flexWrap="wrap">
                  {categories_label
                    .filter((category: any) =>
                      categories.includes(category.value)
                    )
                    .map((category: any) => (
                      <Badge
                        key={category}
                        variant="solid"
                        colorScheme="gray"
                        mr="2"
                      >
                        {category.label}
                      </Badge>
                    ))}
                </Flex>
              </Flex>
            </Flex>
          </Flex>
        </>
      )}

      {isLargerThan900 ? (
        <SimpleGrid columns={[1, 2]} gap="3">
          <PriceHistory id={id} type="retail" />
          <PriceHistory id={id} type="whole_sale" />
        </SimpleGrid>
      ) : (
        <Tabs isFitted>
          <TabList>
            <Tab _selected={{ color: "white", bg: "primary" }}>Retail</Tab>
            <Tab _selected={{ color: "white", bg: "primary" }}>Whole Sale</Tab>
          </TabList>

          <TabPanels>
            <TabPanel>
              <PriceHistory id={id} type="retail" />
            </TabPanel>
            <TabPanel>
              <PriceHistory id={id} type="whole_sale" />
            </TabPanel>
          </TabPanels>
        </Tabs>
      )}

      {isPublic && !isEmpty(categories) && (
        <Box p="3" height="100vh" overflow="auto-scroll">
          <Text fontSize="2xl" my="5" color="gray" textAlign="center">
            Similar Commodities
          </Text>

          <PublicCommoditiesTable
            isPublic={isPublic}
            similar_categories={categories}
            showFilters={false}
          />
        </Box>
      )}
    </UserLayout>
  );
};

export default CommodityDetails;
